import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { useSearchParams } from 'react-router-dom';
import { fetchChartData, fetchCibilOverview } from '../../../../../../store/dashboard/creditBureau/actions';
import { COMBINED_REPORT, DETAILED_REPORT } from '../../CreditBureau';
import clsx from 'clsx';
import styles from './Report.module.scss';
import { DASHBOARD_DATA_TYPES } from '../../../..';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { getSubdomain } from '../../../../../../utils/utils';

const useReport = ({
    selectedReport,
    selectedDirector,
    setSelectedReport,
    selectedReportConfig,
    selectedCompany,
    getGridRowHeight,
    resetQueryParams,
    creditBureauConfig,
}) => {
    const dispatch = useAppDispatch();
    const creditBureauData = useAppSelector((state) => state.dashboard.creditBureau);
    const chartsData = creditBureauData.charts;
    const cibilOverviewData = creditBureauData.cibilOverviewData;
    const directorsList = creditBureauData?.mainData?.listDirectorsOverview || [];
    const user = useAppSelector((state) => state.user);
    const [searchParams, setSearchParams] = useSearchParams();
    const [dataType, setDataType] = useState(DASHBOARD_DATA_TYPES.CHARTS);
    const [isBureauEnquiryDrawerOpen, setIsBureauEnquiryDrawerOpen] = useState(false);
    const bureauType = searchParams.get('bureauType') == 'All' ? null : searchParams.get('bureauType');
    const directorId = searchParams.get('directorId');
    const isCombinedReport = selectedReport === COMBINED_REPORT;

    useEffect(() => {
        fetchCibilOverviewData();
        return () => resetQueryParams();
    }, []);

    const getRiskClassname = (isDpd, isFirstTwo = false) => {
        return clsx({
            [styles.MetricValue]: true,
            [styles.LowRiskBadge]: !isDpd,
            [styles.MediumRiskBadge]: false,
            [styles.HighRiskBadge]: isDpd,
            [styles.GrayPill]: isFirstTwo,
        });
    };
    function getImageSource(director, bureauType) {
        if (!director && !bureauType) return null;
        return creditBureauConfig?.tabsToDisplay?.[bureauType ?? director?.creditBureau]?.logoUrl;
    }

    const fetchCibilOverviewData = () => {
        const directorId = searchParams.get('directorId');
        dispatch(
            fetchCibilOverview({
                assesseeOrgId: selectedCompany?.assesseeOrgId,
                reportId: selectedCompany?.reportId || '',
                ...(directorId && { directorId: directorId }),
                type: searchParams.get('bureauType'),
            }),
        );
    };

    const openBureauEnquiryDrawer = () => {
        mixpanelBureauEnquiryDrawerClick();
        searchParams.set('openBureauEnquiryDrawer', 'true');
        searchParams.set('directorIdEnquiryDrawer', selectedDirector?.directorId);
        {
            selectedDirector?.creditBureau && searchParams.set('vendorTypeEnquiryDrawer', selectedDirector?.creditBureau);
        }
        setSearchParams(searchParams);
        setIsBureauEnquiryDrawerOpen(true);
    };

    const closeBureauEnquiryDrawer = () => {
        searchParams.delete('openBureauEnquiryDrawer');
        searchParams.delete('directorIdEnquiryDrawer');
        searchParams.delete('vendorTypeEnquiryDrawer');
        setSearchParams(searchParams);
        setIsBureauEnquiryDrawerOpen(false);
    };

    const fetchGraph = (graphName) => {
        const graphNameAry = [graphName];
        const directorId = searchParams.get('directorId');
        let type: string | null = null;
        if (!directorId) {
            type = searchParams.get('bureauType') == 'All' ? null : searchParams.get('bureauType');
        }
        dispatch(
            fetchChartData({
                graphNames: graphNameAry,
                tabName: 'creditBureau',
                startDate: '',
                endDate: '',
                tenure: '',
                reportId: selectedCompany?.reportId || '',
                ...(directorId && { directorId: directorId }),
                type: type,
            }),
        );
    };

    const mixpanelBureauEnquiryDrawerClick = () => {
        const properties = {
            'User Role': user.userData.accountType,
            'PNO Platform': getSubdomain(),
            'User id': user.userData.userId,
            'Current URL': window.location.href,
            'Company Name': selectedCompany?.orgName,
            'Organization ID': selectedCompany?.assesseeOrgId,
            'Bureau Name': selectedDirector?.creditBureau,
        };

        Mixpanel.track('Bureau Enquiry Drawer Clicked', properties);
    };

    const getReportTitle = () => {
        switch (selectedReport) {
            case COMBINED_REPORT:
                return 'Combined Report';
            case DETAILED_REPORT:
                return selectedDirector ? selectedDirector?.name : 'Detailed Report';
            default:
                return '';
        }
    };

    const keyOfficialsCount = () => {
        const directorsCount = directorsList?.length;
        if (selectedReport === COMBINED_REPORT && directorsCount) {
            return ` (${directorsCount > 1 ? 'all ' : ''}${directorsCount} key official${directorsCount > 1 ? 's' : ''})`;
        }
        return '';
    };

    const getRiskVariantFromRiskCategory = (riskCategory) => {
        switch (riskCategory) {
            case 'Low Risk':
                return 'Low';
            case 'Moderate Risk':
                return 'Medium';
            case 'High Risk':
                return 'High';
            default:
                return 'Low';
        }
    };

    return {
        //State
        user,
        searchParams,
        chartsData,
        cibilOverviewData,
        dataType,
        isBureauEnquiryDrawerOpen,
        bureauType,
        directorId,
        selectedDirector,
        selectedReport,
        setSelectedReport,
        selectedReportConfig,
        resetQueryParams,
        isCombinedReport,

        //Methods
        setDataType,
        getImageSource,
        openBureauEnquiryDrawer,
        closeBureauEnquiryDrawer,
        fetchGraph,
        getReportTitle,
    };
};

export default useReport;
